import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function CTASection() {
  const { t } = useTranslation();
  return (
    <section className="py-12 sm:py-16 md:py-24 lg:py-32 bg-white relative overflow-hidden border-t border-slate-100">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 relative">
        <div className="relative bg-gradient-to-r from-[#10172A] to-[#12192D] rounded-xl sm:rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 xl:p-20 overflow-hidden border border-slate-800/50 shadow-lg group">
          {/* Background Elements */}
          <div className="absolute top-0 right-0 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-blue-500/10 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-emerald-500/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('/images/grid.svg')] opacity-[0.03] pointer-events-none"></div>

          <div className="relative z-10 text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full mb-4 sm:mb-5 md:mb-6 border border-blue-200 shadow-sm">
              <div className="w-2 h-2 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full animate-pulse"></div>
              <span className="text-xs sm:text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {t('cta.badge')}
              </span>
            </div>

            {/* Headline */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-5 md:mb-6 leading-tight px-2" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              {t('cta.title')}<br className="hidden sm:block" />
              <span className="text-white animate-pulse">{t('cta.titleHighlight')}</span>
            </h2>

            {/* Description */}
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8 md:mb-10 leading-relaxed max-w-2xl mx-auto px-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('cta.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-6 sm:mb-8 md:mb-10 px-2">
              <button
                onClick={() => {
                  const element = document.getElementById('preise');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="group px-6 py-3 sm:px-7 sm:py-3.5 md:px-8 md:py-4 bg-white text-[#1E3A8A] rounded-full shadow-lg hover:bg-slate-50 transition-all duration-300 hover:scale-105 whitespace-nowrap font-bold text-sm sm:text-base md:text-lg flex items-center justify-center gap-2"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {t('cta.ctaPrimary')}
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </button>
              <Link
                to="/demo"
                className="px-6 py-3 sm:px-7 sm:py-3.5 md:px-8 md:py-4 border border-white/20 bg-white/10 text-white backdrop-blur-sm rounded-full hover:bg-white/20 hover:border-white/30 transition-all duration-300 whitespace-nowrap font-semibold text-sm sm:text-base md:text-lg flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                <i className="ri-play-circle-line text-base sm:text-lg md:text-xl text-white"></i>
                {t('cta.ctaSecondary')}
              </Link>
            </div>

            {/* Features */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 md:gap-6 lg:gap-8 text-white px-2">
              <div className="flex items-center gap-2 group/check">
                <i className="ri-check-line text-emerald-300 text-base sm:text-lg md:text-xl group-hover/check:scale-110 transition-transform"></i>
                <span className="text-xs sm:text-sm md:text-base font-medium whitespace-nowrap text-white/90 group-hover/check:text-white transition-colors" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {t('cta.benefit1')}
                </span>
              </div>
              <div className="flex items-center gap-2 group/check">
                <i className="ri-check-line text-emerald-300 text-base sm:text-lg md:text-xl group-hover/check:scale-110 transition-transform"></i>
                <span className="text-xs sm:text-sm md:text-base font-medium whitespace-nowrap text-white/90 group-hover/check:text-white transition-colors" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  {t('cta.benefit2')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
